import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Check, Loader2, Sparkles, Users, Swords, X } from "lucide-react";
import { validateUsername, RESERVED_USERNAMES } from "@/lib/username";
import SEO from "@/components/SEO";

type Status = "idle" | "checking" | "available" | "taken" | "invalid" | "reserved";

export default function CreateUsername() {
  const { user } = useAuth();
  const { profile, loading: profileLoading, refetch } = useProfile();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/dashboard";

  const [username, setUsername] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // If they already have one, bounce away.
  useEffect(() => {
    if (!profileLoading && profile?.username) navigate(next, { replace: true });
  }, [profile, profileLoading, navigate, next]);

  // Debounced availability check.
  useEffect(() => {
    setErrorMsg(null);
    if (!username) {
      setStatus("idle");
      return;
    }
    const v = validateUsername(username);
    if (v.ok !== true) {
      setStatus(v.reason === "reserved" ? "reserved" : "invalid");
      setErrorMsg(v.message);
      return;
    }
    setStatus("checking");
    const t = setTimeout(async () => {
      const { data, error } = await supabase.rpc("is_username_available", {
        p_username: username,
        p_user_id: user?.id ?? null,
      });
      if (error) {
        setStatus("idle");
        setErrorMsg("Couldn't check availability. Try again.");
      } else if (data) {
        setStatus("available");
      } else {
        setStatus("taken");
        setErrorMsg("That username is already taken. Try another.");
      }
    }, 350);
    return () => clearTimeout(t);
  }, [username, user]);

  const canSave = !!user && status === "available" && !saving;

  const handleSave = async () => {
    if (!user) return;
    const v = validateUsername(username);
    if (v.ok !== true) {
      toast.error(v.message);
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .upsert({ id: user.id, username, discoverable: true }, { onConflict: "id" });
    setSaving(false);
    if (error) {
      const msg = /reserved/i.test(error.message)
        ? "That username is reserved. Please choose another."
        : /unique|duplicate/i.test(error.message)
        ? "That username is already taken. Try another."
        : error.message || "Something went wrong while saving your username. Please try again.";
      toast.error(msg);
      return;
    }
    toast.success("Username saved!");
    await refetch();
    navigate(next, { replace: true });
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
      <SEO
        title="Create your username — HeirWise"
        description="Pick the username friends will use to find you, challenge you, and follow your progress on HeirWise."
        path="/create-username"
      />
      <div className="w-full max-w-md">
        <div className="glass-card glow-border p-6 sm:p-8 space-y-6 animate-scale-in">
          <div className="text-center space-y-3">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 border border-primary/20">
              <Sparkles className="w-3 h-3 text-primary" />
              <span className="text-[10px] uppercase tracking-wider text-primary font-semibold">
                Last step
              </span>
            </div>
            <h1 className="font-heading font-extrabold text-2xl sm:text-3xl text-foreground">
              Create Your Username
            </h1>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-sm mx-auto">
              Choose the name friends will use to find you, challenge you, and follow your progress.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-xl border border-border bg-card/50 p-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" />
              <span className="text-xs text-foreground">Add & search friends</span>
            </div>
            <div className="rounded-xl border border-border bg-card/50 p-3 flex items-center gap-2">
              <Swords className="w-4 h-4 text-primary" />
              <span className="text-xs text-foreground">Send challenges</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="username" className="text-xs font-medium text-foreground">
              Username
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-base text-muted-foreground pointer-events-none">
                @
              </span>
              <Input
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value.replace(/\s/g, ""))}
                placeholder="yourhandle"
                maxLength={20}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                className="h-12 pl-8 text-base"
                disabled={saving}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2">
                {status === "checking" && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                {status === "available" && <Check className="w-4 h-4 text-emerald-400" />}
                {(status === "taken" || status === "invalid" || status === "reserved") && (
                  <X className="w-4 h-4 text-destructive" />
                )}
              </span>
            </div>
            <p
              className={
                errorMsg
                  ? "text-xs text-destructive"
                  : status === "available"
                  ? "text-xs text-emerald-400"
                  : "text-xs text-muted-foreground"
              }
            >
              {errorMsg ??
                (status === "available"
                  ? "Available!"
                  : "Use 3–20 characters. Letters, numbers, underscores, and periods only.")}
            </p>
          </div>

          <Button onClick={handleSave} disabled={!canSave} className="w-full h-12 text-sm font-semibold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Username"}
          </Button>

          <p className="text-[11px] text-muted-foreground text-center leading-relaxed">
            You can use this username to connect with trusted friends and join challenges.
          </p>
        </div>
      </div>
    </div>
  );
}

// Suppress unused-import warning if tree-shaking complains.
export const __reservedCount = RESERVED_USERNAMES.length;